<template lang="pug">
v-card
  v-card-title
    span Venta vs. Ganancia por producto
    ShowInfoBtn(@click="$refs.infoAlert.showAlert()")
  v-card-text
    InfoAlert(:message="alertMessage" ref="infoAlert")

    //- Insight automático (el "momento ajá"): un producto que rankea distinto
    //- en venta que en ganancia.
    v-alert(v-if="ahaMessage" dense text color="primary" border="left" class="mb-4")
      span(v-html="ahaMessage")

    v-row(v-if="salesRanked.length")
      //- Columna: ranking por VENTA
      v-col(cols="12" md="6")
        .rank-head.mb-2
          span.rank-title POR VENTA
          span.rank-sub Facturación potencial
        .rank-row(v-for="(p, i) in salesRanked" :key="'s'+p.name")
          span.rank-pos {{ i + 1 }}
          .rank-body
            .rank-name {{ p.name }}
            .rank-val {{ p.value | moneyMM }}
          span.move(:class="moveClass(p.name)") {{ moveLabel(p.name) }}

      //- Columna: ranking por GANANCIA
      v-col(cols="12" md="6")
        .rank-head.mb-2
          span.rank-title POR GANANCIA
          span.rank-sub Margen bruto
        .rank-row(v-for="(p, i) in marginRanked" :key="'m'+p.name")
          span.rank-pos.rank-pos--profit {{ i + 1 }}
          .rank-body
            .rank-name {{ p.name }}
            .rank-val {{ p.value | moneyMM }}
          span.move(:class="moveClass(p.name)") {{ moveLabel(p.name) }}

    .empty-rank(v-else)
      p.text--secondary.mb-0 Aún no hay datos suficientes para comparar venta y ganancia.
</template>

<script>
import { mapActions } from 'vuex'
import InfoAlert from '../../common/InfoAlert.vue'
import ShowInfoBtn from '../../common/ShowInfoBtn.vue'

export default {
  props: {
    filters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    projection: {
      type: Number,
      default: null,
    },
  },
  components: {
    InfoAlert,
    ShowInfoBtn,
  },
  data() {
    return {
      sales: [],
      margins: [],
      alertMessage: `
<p>
Este comparativo muestra tus productos ordenados de dos formas: por <b>venta</b> (cuánto facturan) y por <b>ganancia</b> (cuánto margen bruto dejan). Cuando un producto sube o baja de posición entre una lista y la otra, es una señal: <b>vender mucho no siempre es ganar más</b>. Concentra tu esfuerzo donde está la ganancia.
</p>`,
    }
  },
  computed: {
    salesRanked() {
      return [...this.sales]
        .filter(p => p.product_name && p.total > 0)
        .sort((a, b) => b.total - a.total)
        .map(p => ({ name: p.product_name, value: p.total }))
    },
    marginRanked() {
      return [...this.margins]
        .filter(p => p.product_name && p.total_profit > 0)
        .sort((a, b) => b.total_profit - a.total_profit)
        .map(p => ({ name: p.product_name, value: p.total_profit }))
    },
    salesRank() {
      const m = {}
      this.salesRanked.forEach((p, i) => { m[p.name] = i + 1 })
      return m
    },
    marginRank() {
      const m = {}
      this.marginRanked.forEach((p, i) => { m[p.name] = i + 1 })
      return m
    },
    // El "momento ajá": el producto que más baja de la lista de venta a la de ganancia.
    ahaMessage() {
      let worst = null
      this.salesRanked.forEach(p => {
        const s = this.salesRank[p.name]
        const g = this.marginRank[p.name]
        if (!g) return
        const drop = g - s
        if (drop > 0 && (!worst || drop > worst.drop)) {
          worst = { name: p.name, s, g, drop }
        }
      })
      if (!worst) return null
      const riser = this.marginRanked[worst.g - 2] // el que quedó justo arriba de él en ganancia
      const riserTxt = riser ? ` — por debajo de <b>${this.pretty(riser.name)}</b>` : ''
      return `Ojo: <b>${this.pretty(worst.name)}</b> es tu #${worst.s} en venta, pero en <b>ganancia cae al #${worst.g}</b>${riserTxt}. Vender mucho no siempre es ganar más.`
    },
  },
  methods: {
    ...mapActions('commercial_projection', ['getTopProductsTable', 'getOptimalMixTable']),
    pretty(name) {
      if (!name) return ''
      return name.charAt(0) + name.slice(1).toLowerCase()
    },
    // Indicador = propiedad del producto: cuántos puestos gana (o pierde) en el
    // ranking de GANANCIA respecto al de VENTA. Positivo (▲ verde) = deja más de lo
    // que vende; negativo (▼ coral) = vende más de lo que deja. Igual en ambas listas.
    move(name) {
      const s = this.salesRank[name]
      const g = this.marginRank[name]
      if (!s || !g) return null
      return s - g
    },
    moveLabel(name) {
      const d = this.move(name)
      if (d === null) return ''
      if (d === 0) return '='
      return (d > 0 ? '▲ ' : '▼ ') + Math.abs(d)
    },
    moveClass(name) {
      const d = this.move(name)
      if (d === null || d === 0) return 'move--flat'
      return d > 0 ? 'move--up' : 'move--down'
    },
    async fetchData() {
      const params = {
        ...this.filters,
        projection: this.projection || this.$route.params.id,
      }
      const [ventaRes, mixRes] = await Promise.all([
        this.getTopProductsTable(params),
        this.getOptimalMixTable({ ...params, page: 1, page_size: 100 }),
      ])
      if (!ventaRes.error) this.sales = ventaRes.data || []
      if (!mixRes.error) this.margins = (mixRes.data && mixRes.data.data) || []
    },
  },
  watch: {
    filters: {
      handler() { this.fetchData() },
      deep: true,
    },
  },
  created() {
    this.fetchData()
  },
}
</script>

<style scoped>
.rank-head { display: flex; align-items: baseline; gap: 10px; border-bottom: 2px solid #ECEDF0; padding-bottom: 6px; }
.rank-title { font-weight: 600; letter-spacing: .06em; font-size: 13px; color: #1B2E38; }
.rank-sub { font-size: 12px; color: #8A99A3; }
.rank-row { display: flex; align-items: center; gap: 12px; padding: 10px 4px; border-bottom: 1px solid #F0F2F7; }
.rank-pos {
  flex: none; width: 26px; height: 26px; border-radius: 50%;
  display: grid; place-items: center; font-weight: 700; font-size: 13px;
  background: #EAEEFC; color: #5575DF;
}
.rank-pos--profit { background: #E1F6F1; color: #0FA890; }
.rank-body { flex: 1 1 auto; min-width: 0; }
.rank-name { font-size: 14px; color: #1B2E38; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rank-val { font-size: 12.5px; color: #4A5A66; font-variant-numeric: tabular-nums; }
.move { flex: none; font-size: 12px; font-weight: 600; min-width: 34px; text-align: right; }
.move--up { color: #0FA890; }
.move--down { color: #E0533C; }
.move--flat { color: #B9C4CC; }
.empty-rank { border: 1px dashed #D6DAE3; border-radius: 10px; padding: 24px; text-align: center; background: #FAFAFB; }
</style>
